<template>
  <div class="text-input-card" :class="cardClass">
    <!-- 标题栏 -->
    <div class="text-input-card__titlebar">
      <span class="text-input-card__titlebar-icon">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" stroke-width="1.8"/>
          <path d="M7 9h10M7 13h6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
      </span>
      <span class="text-input-card__titlebar-text">{{ card.question }}</span>
      <span class="text-input-card__status-badge">{{ statusBadge }}</span>
    </div>

    <!-- 输入区域（pending 且本地未提交时显示） -->
    <div v-if="card.status === 'pending' && localSubmitted === null" class="text-input-card__body">
      <textarea
        v-model="inputValue"
        class="text-input-card__textarea"
        :placeholder="card.placeholder || '请输入...'"
        :maxlength="card.maxLength || undefined"
        rows="3"
        @keydown.enter.ctrl.prevent="onSubmit"
      ></textarea>
      <div v-if="card.maxLength" class="text-input-card__counter">
        {{ inputValue.length }} / {{ card.maxLength }}
      </div>
    </div>

    <!-- 已提交状态 -->
    <div v-else-if="card.status === 'submitted' || localSubmitted !== null" class="text-input-card__submitted">
      <span class="text-input-card__submitted-icon">✓</span>
      <span class="text-input-card__submitted-text">{{ localSubmitted || card.submittedValue }}</span>
    </div>

    <!-- 已过期 -->
    <div v-else class="text-input-card__submitted text-input-card__submitted--expired">
      <span class="text-input-card__submitted-text">已超时取消</span>
    </div>

    <!-- 操作按钮（pending 且本地未提交时显示） -->
    <div v-if="card.status === 'pending' && localSubmitted === null" class="text-input-card__footer">
      <button
        class="text-input-card__btn text-input-card__btn--cancel"
        :disabled="submitting"
        @click="onCancel"
      >
        取消
      </button>
      <button
        class="text-input-card__btn text-input-card__btn--submit"
        :disabled="submitting || !inputValue.trim()"
        @click="onSubmit"
      >
        <span v-if="submitting" class="text-input-card__btn-spinner"></span>
        <span v-else>提交</span>
      </button>
    </div>
  </div>
</template>

<script>
import { computed, ref } from '@vue/composition-api';

export default {
  name: 'TextInputCard',
  props: {
    card: {
      type: Object,
      required: true,
    },
  },
  emits: ['action'],
  setup(props, { emit }) {
    const inputValue = ref(props.card.defaultValue || '');
    const localSubmitted = ref(null); // null | string（已提交的值）
    const submitting = ref(false);

    const cardClass = computed(() => {
      const effectiveStatus = localSubmitted.value !== null ? 'submitted' : props.card.status;
      return `text-input-card--${effectiveStatus}`;
    });

    const statusBadge = computed(() => {
      const effectiveStatus = localSubmitted.value !== null ? 'submitted' : props.card.status;
      const map = { pending: '待输入', submitted: '已提交', expired: '已超时' };
      return map[effectiveStatus] || '';
    });

    function onSubmit() {
      if (submitting.value || !inputValue.value.trim()) return;
      submitting.value = true;
      localSubmitted.value = inputValue.value.trim();
      emit('action', props.card.cardId, 'submit', inputValue.value.trim());
    }

    function onCancel() {
      if (submitting.value) return;
      emit('action', props.card.cardId, 'cancel', undefined);
    }

    return { inputValue, localSubmitted, submitting, cardClass, statusBadge, onSubmit, onCancel };
  },
};
</script>

<style scoped>
.text-input-card {
  background: #fff;
  border-radius: 12px;
  border: 1.5px solid #d8e3f5;
  overflow: hidden;
  margin: 4px 0;
  box-shadow: 0 2px 8px rgba(0, 82, 217, 0.08);
  width: 100%;
}
.text-input-card--submitted { border-color: #b7ebc8; box-shadow: 0 2px 8px rgba(7, 193, 96, 0.08); }
.text-input-card--expired { border-color: #e8ecf0; box-shadow: none; opacity: 0.75; }

.text-input-card__titlebar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%);
  border-bottom: 1px solid #d8e3f5;
}
.text-input-card__titlebar-icon {
  display: flex;
  align-items: center;
  color: #0052D9;
  flex-shrink: 0;
}
.text-input-card__titlebar-text {
  font-size: 13px;
  font-weight: 600;
  color: #1a202c;
  flex: 1;
  line-height: 1.4;
  word-break: break-word;
}
.text-input-card__status-badge {
  font-size: 11px;
  padding: 2px 7px;
  border-radius: 999px;
  background: #e8f0fe;
  color: #0052D9;
  font-weight: 500;
  white-space: nowrap;
}
.text-input-card--submitted .text-input-card__status-badge { background: #e8f8ef; color: #07c160; }
.text-input-card--expired .text-input-card__status-badge { background: #f5f7fa; color: #86909c; }

.text-input-card__body {
  padding: 6px 14px 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.text-input-card__textarea {
  width: 100%;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  color: #1a202c;
  line-height: 1.6;
  resize: none;
  outline: none;
  background: #f8faff;
  box-sizing: border-box;
  transition: border-color 0.15s ease;
  font-family: inherit;
}
.text-input-card__textarea:focus {
  border-color: #0052D9;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(0, 82, 217, 0.08);
}
.text-input-card__counter {
  font-size: 11px;
  color: #86909c;
  text-align: right;
}

.text-input-card__submitted {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 10px 14px 12px;
}
.text-input-card__submitted--expired { opacity: 0.6; }
.text-input-card__submitted-icon { font-size: 14px; color: #07c160; flex-shrink: 0; margin-top: 1px; }
.text-input-card__submitted-text {
  font-size: 13px;
  color: #4a5568;
  white-space: pre-wrap;
  word-break: break-word;
}

.text-input-card__footer {
  display: flex;
  gap: 8px;
  padding: 8px 14px 12px;
}
.text-input-card__btn {
  flex: 1;
  height: 34px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.15s ease;
}
.text-input-card__btn--cancel { background: #f5f7fa; color: #4a5568; border: 1px solid #e2e8f0; }
.text-input-card__btn--cancel:hover { background: #eef0f3; }
.text-input-card__btn--submit {
  background: linear-gradient(135deg, #0052D9 0%, #2979FF 100%);
  color: #fff;
  box-shadow: 0 2px 6px rgba(0, 82, 217, 0.3);
}
.text-input-card__btn--submit:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
.text-input-card__btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none !important; }

.text-input-card__btn-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: text-input-spin 0.6s linear infinite;
  vertical-align: middle;
}
@keyframes text-input-spin {
  to { transform: rotate(360deg); }
}
</style>
